import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Drawer,
  IconButton,
  Alert,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation } from "react-router-dom";
import APIHelper from "./utils/APIHelper";
import TopBar from "../LandingPage/TopBar";
import MessageCounter from "./MessageCounter";
import PageSelector from "./PageSelector";

import { Routes, Route, Navigate } from "react-router-dom";
import DashboardHomePage from "./pages/DashboardHomePage";
import SchedulerPage from "./pages/SchedulerPage";
import InventoryPage from "./pages/InventoryPage";
import ProductsPage from "./pages/ProductsPage";
import OrdersPage from "./pages/OrdersPage";
import ShippingPage from "./pages/ShippingPage";
import CampaignsPage from "./pages/CampaignsPage";
import MarketingPage from "./pages/MarketingPage";
import FinancePage from "./pages/FinancePage";
import SettingsPage from "./pages/SettingsPage";
import AIAutoCommentPage from "./pages/AIAutoCommentPage";
import BillingPage from "./pages/BillingPage";
import HelpPage from "./pages/HelpPage";
import AIToolsPage from "./pages/AIToolsPage";
import CustomersPage from "./pages/CustomersPage";
import WhatsAppSettingsPage from "./pages/WhatsAppSettingsPage";
import FeedbackModal from "../LandingPage/FeedbackModal";
import { trackEvent } from "../analytics";
// Footer intentionally not used on dashboard pages
import SidebarNav from "./SidebarNav";
import AutoCommentConfigPage from "./pages/AutoCommentConfigPage";
import {
  DiscoveryProvider,
  useDiscovery,
} from "./components/discovery/DiscoveryContext";
import DiscoveryWizard from "./components/discovery/DiscoveryWizard";
import ProgressIndicator from "./components/discovery/ProgressIndicator";

const USE_MOCK_PAGE = process.env.REACT_APP_USE_MOCK_PAGE === "true";


const MOCK_PROFILE = {
  name: "Dev User",
};

const MOCK_PAGES = [
  {
    id: "mock-page-1",
    name: "Mock Facebook Page (Dev)",
  },
];

const MOCK_SELECTED_PAGE_ID = "mock-page-1";

const AppPage = ({ token }) => {
  const [profileData, setProfileData] = useState(
    USE_MOCK_PAGE ? MOCK_PROFILE : null
  );
  const [pages, setPages] = useState(USE_MOCK_PAGE ? MOCK_PAGES : []);
  const [selectedPage, setSelectedPage] = useState(
    USE_MOCK_PAGE ? MOCK_SELECTED_PAGE_ID : null
  );
  const [managedPages, setManagedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [profileError, setProfileError] = useState(false);
  const currentSection = location.pathname.split("/")[2] || "";

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    // Support path-based sections like /dashboard/orders
    const parts = location.pathname.split("/");
    const section = parts.length >= 3 ? parts[2] : "";
    if (section) {
      const el = document.getElementById(section);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (token && !USE_MOCK_PAGE) {
      fetchProfileAndPages(token);
      fetchManagedPages(token);
    }
  }, [token]);

  useEffect(() => {
    if (token && !USE_MOCK_PAGE) {
      fetchPreferredPage(token);
    }
  }, [token]);

  const fetchProfileAndPages = async (token) => {
    setLoading(true);
    setProfileError(false);
    try {
      // profile
      try {
        const profileResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (profileResponse.status === 200) {
          setProfileData(profileResponse.data);
        }
      } catch (err) {
        setProfileError(true);
        setProfileData(null);
        console.warn(
          "Profile fetch failed:",
          err?.response?.data || err?.message
        );
      }

      // pages
      try {
        const pagesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/pages`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (pagesResponse.status === 200) {
          setPages(pagesResponse.data.data);
        }
      } catch (err) {
        console.warn(
          "Pages fetch failed:",
          err?.response?.data || err?.message
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferredPage = async (token) => {
    if (USE_MOCK_PAGE) {
      return;
    }
    try {
      const preferredPage = await APIHelper.fetchPreferredPage(token);
      setSelectedPage(preferredPage);
    } catch (error) {
      console.error("Error fetching preferred page:", error);
    }
  };

  const fetchManagedPages = async (token) => {
    if (USE_MOCK_PAGE) {
      return;
    }
    try {
      const pages = await APIHelper.getManagedPages(token);
      setManagedPages(pages);
    } catch (error) {
      console.error("Error fetching managed pages:", error);
    }
  };

  const handlePageSelect = async (pageId) => {
    setSelectedPage(pageId);
    if (USE_MOCK_PAGE) {
      return;
    }
    try {
      // Add to managed pages if not already there
      if (!managedPages.includes(pageId)) {
        await APIHelper.addManagedPage(token, pageId);
        setManagedPages([...managedPages, pageId]);
      }
      await APIHelper.updatePreferredPage(token, pageId);
      trackEvent("Page Configured", { pageId });
    } catch (error) {
      console.error("Error updating preferred page:", error);
      trackEvent("Page Config Error", { pageId, error: error.message });
    }
  };

  const handleAddChannel = () => {
    // Clear selected page to show PageSelector with channel management
    setSelectedPage(null);
  };

  return (
    <DiscoveryProvider token={token}>
      <TopBar
        onClearConfiguration={async () => {
          try {
            await APIHelper.clearPreferredPage(token);
            await APIHelper.clearAutoCommentConfig(token);
            setSelectedPage(null);
          } catch (error) {
            console.error("Error clearing configuration:", error);
          }
        }}
        onDisconnectFB={async () => {
          try {
            await APIHelper.disconnectFacebook(token);
            window.location.href = "/app";
          } catch (error) {
            console.error("Error clearing configuration:", error);
          }
        }}
        isConfigured={!!selectedPage}
        token={token}
        pages={pages}
        selectedPage={selectedPage}
        managedPages={managedPages}
        onPageSelect={handlePageSelect}
        onAddChannel={handleAddChannel}
      />

      <Box className="flex min-h-[calc(100vh-64px)] bg-gray-50 dark:bg-gray-900 overflow-hidden">
        {/* Sidebar: permanent on md+, temporary on mobile */}
        <Drawer
          variant="permanent"
          className="hidden md:block"
          sx={{
            "& .MuiDrawer-paper": {
              position: "relative",
              width: 260,
              boxSizing: "border-box",
              borderRight: "none",
              backgroundColor: "transparent",
              padding: 0,
              margin: 0,
              overflow: "hidden",
              boxShadow: "2px 0 4px rgba(0, 0, 0, 0.05)",
              zIndex: 0,
            },
          }}
          PaperProps={{
            className: "bg-white dark:bg-gray-800",
            style: { padding: 0, margin: 0, borderRight: "none" },
          }}
          open
        >
          <SidebarNav onNavigate={(to) => routerNavigate(to)} />
        </Drawer>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          className="block md:hidden"
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: 260,
              boxSizing: "border-box",
              backgroundColor: "transparent",
              padding: 0,
              margin: 0,
              borderRight: "none",
            },
          }}
          PaperProps={{
            className: "bg-white dark:bg-gray-800",
            style: { padding: 0, margin: 0, borderRight: "none" },
          }}
        >
          <SidebarNav
            onNavigate={(to) => {
              setMobileOpen(false);
              routerNavigate(to);
            }}
          />
        </Drawer>

        {/* Main content */}
        <Box
          component="main"
          className="flex-grow w-full md:w-[calc(100%-260px)]"
        >
          {/* Mobile menu button */}
          <Box className="flex md:hidden p-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="open navigation"
              className="text-gray-700 dark:text-gray-300"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Container className="py-6">
            {loading && (
              <Box className="flex justify-center mt-4">
                <CircularProgress size={24} />
              </Box>
            )}

            {profileError && (
              <Box className="mt-4">
                <Alert severity="warning" className="rounded-xl">
                  We couldn't load your profile info right now. Other features
                  remain available.
                </Alert>
              </Box>
            )}

            <Box className="mt-2">
              {currentSection === "" && (
                <Box className="flex items-center justify-between p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-soft mb-6 border border-gray-100 dark:border-gray-700">
                  <Typography
                    variant="h5"
                    className="text-2xl font-bold text-gray-900 dark:text-white"
                  >
                    Hi {profileData?.name ? profileData.name : "there"}, Welcome
                    back!
                  </Typography>

                  {selectedPage && (
                    <Box className="flex justify-end">
                      <MessageCounter token={token} pageId={selectedPage} />
                    </Box>
                  )}
                </Box>

              )}


              {!selectedPage ? (
                <>
                  <PageSelector
                    selectedPage={selectedPage}
                    pages={pages}
                    onPageSelect={handlePageSelect}
                    managedPages={managedPages}
                    onManagedPagesUpdate={setManagedPages}
                    token={token}
                  />
                  <Routes>
                    <Route
                      path="comments/configure"
                      element={
                        <AutoCommentConfigPage
                          token={token}
                          pageId={selectedPage}
                        />
                      }
                    />
                  </Routes>
                </>
              ) : (
                <Routes>
                  <Route
                    index
                    element={
                      <DashboardHomePage token={token} pageId={selectedPage} selectedPageData={pages.find(p => p.id === selectedPage)} />
                    }
                  />
                  <Route
                    path="scheduler"
                    element={
                      <SchedulerPage token={token} pageId={selectedPage} />
                    }
                  />
                  <Route
                    path="inventory"
                    element={
                      <InventoryPage token={token} pageId={selectedPage} />
                    }
                  />
                  <Route
                    path="products"
                    element={<ProductsPage token={token} />}
                  />
                  <Route
                    path="orders"
                    element={<OrdersPage token={token} pageId={selectedPage} />}
                  />
                  <Route
                    path="shipping"
                    element={
                      <ShippingPage token={token} pageId={selectedPage} />
                    }
                  />
                  <Route
                    path="campaigns"
                    element={<CampaignsPage token={token} />}
                  />
                  <Route path="marketing" element={<MarketingPage />} />
                  <Route
                    path="finance"
                    element={
                      <FinancePage token={token} pageId={selectedPage} />
                    }
                  />
                  <Route
                    path="auto-comment"
                    element={<AIAutoCommentPage token={token} />}
                  />
                  <Route
                    path="customers"
                    element={<CustomersPage token={token} />}
                  />
                  <Route
                    path="whatsapp"
                    element={<WhatsAppSettingsPage token={token} />}
                  />
                  <Route
                    path="ai"
                    element={
                      <AIToolsPage token={token} pageId={selectedPage} />
                    }
                  />
                  <Route
                    path="settings"
                    element={<SettingsPage token={token} />}
                  />
                  <Route
                    path="billing"
                    element={<BillingPage token={token} />}
                  />
                  <Route path="help" element={<HelpPage />} />
                  <Route
                    path="comments/configure"
                    element={
                      <AutoCommentConfigPage
                        token={token}
                        pageId={selectedPage}
                        selectedPageData={pages.find(p => p.id === selectedPage)}
                      />
                    }
                  />
                  <Route path="*" element={<Navigate to="." replace />} />
                </Routes>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
      <WizardWrapper />
    </DiscoveryProvider>
  );
};

const WizardWrapper = () => {
  const { wizardOpen, setWizardOpen } = useDiscovery();
  return (
    <DiscoveryWizard
      isOpen={wizardOpen}
      onClose={() => setWizardOpen(false)}
    />
  );
};

export default AppPage;
